import styled from '@emotion/styled';

import useCheckMediaQuery from 'hooks/useCheckMediaQuery';
import { RevenuesTopMyAssetsResponseDto } from 'services/hooks/revenue';
import { isNotNull } from 'utils/predicate';

import ArrowButton from 'components/button/ArrowButton';
import RankingImage from 'components/image/RankingImage';
import Condition from 'components/util/Condition';
import EmptyView from 'components/util/EmptyView';
import Flex from 'components/util/Flex';

import { useRankContext } from 'pages/dashboard/hooks/useRankContext';
import useRankingSlider from 'pages/dashboard/hooks/useRankingSlider';

type RankListSliderProps = {
  data: {
    entire: RevenuesTopMyAssetsResponseDto[];
    my: RevenuesTopMyAssetsResponseDto[];
  };
};

const RankListSlider = ({ data }: RankListSliderProps) => {
  const { assetScope } = useRankContext();

  const {
    loaded,
    instanceRef,
    sliderRef,
    currentSlide,
    isExceedContainerWidth,
    translateLeft,
    translateRight,
    rankList,
  } = useRankingSlider(data[assetScope]);

  const { isDesktop } = useCheckMediaQuery();
  const isShowArrow = loaded && isNotNull(instanceRef.current) && isDesktop && isExceedContainerWidth;

  return (
    <Condition condition={data[assetScope]?.length > 0} fallback={<EmptyView text="No more data" />}>
      <Flex align="center" justify="space-around" gap={22}>
        <SArrowButtonContainer $isShow={isShowArrow}>
          {isShowArrow && (
            <SArrowButton
              disabled={currentSlide === 0}
              direction="left"
              onClick={translateLeft}
              $isShow={isShowArrow}
            />
          )}
        </SArrowButtonContainer>
        <div
          ref={sliderRef}
          className="keen-slider"
          key={`${assetScope}-${rankList.length}-${rankList.map((item) => item.sourceName).join(',')}`} // 데이터 고유 키 생성
        >
          {rankList.map((item) => (
            <RankingItem
              imageSrc={item.coverImageUrl}
              key={item.rank + item.sourceName}
              className={`keen-slider__slide number-slide${item.rank}`}
              rank={item.rank}
              name={item.sourceName}
            />
          ))}
        </div>
        <SArrowButtonContainer $isShow={isShowArrow}>
          {isShowArrow && (
            <SArrowButton
              disabled={currentSlide === instanceRef?.current!.track?.details?.slides?.length - 1}
              direction="right"
              onClick={translateRight}
              $isShow={isShowArrow}
            />
          )}
        </SArrowButtonContainer>
      </Flex>
    </Condition>
  );
};

// 각 랭킹 아이템 UI
type RankingItemProps = {
  className?: string;
  imageSrc: string;
  rank: number;
  name: string;
};
const RankingItem = ({ className, imageSrc, rank, name }: RankingItemProps) => {
  return (
    <SRankingItem
      className={className}
      style={{
        minWidth: '150px',
        maxWidth: '150px',
      }}
    >
      <Flex direction="column" rowGap={16}>
        <RankingImage imageSrc={imageSrc} rank={rank} />
        <p className="title ellipsis-two-line">{name}</p>
      </Flex>
    </SRankingItem>
  );
};

const SRankingItem = styled.div`
  padding: 16px 10px;

  .title {
    ${({ theme }) => theme.font.body1_sb};
    color: ${({ theme }) => theme.color.text.default};
  }
`;

type SArrowButtonContainerProps = {
  $isShow: boolean;
};
const SArrowButtonContainer = styled.div<SArrowButtonContainerProps>`
  min-width: ${({ $isShow }) => ($isShow ? '36px' : 0)};
  position: relative;
  height: 250px;
`;

type SArrowButtonProps = {
  $isShow: boolean;
};
const SArrowButton = styled(ArrowButton)<SArrowButtonProps>`
  width: ${({ $isShow }) => ($isShow ? '36px' : 0)};
  min-width: ${({ $isShow }) => ($isShow ? '36px' : 0)};
  height: 36px;
  border-radius: 50%;
  background: ${({ theme }) => theme.palette.common.white};
  backdrop-filter: blur(2px);
  border: 1px solid ${({ theme }) => theme.color.line.line_1};
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 75px;
`;

export default RankListSlider;
